<template>
  <div v-if="getMqttIsInstanceConnected">
    <div v-if="255 != getMode">
      >
      <div :style="powerStyle">
        <Popper
          class="labelStyle"
          style="position: relative; width: 100%"
          arrow
          offsetDistance="0"
          placement="bottom"
        >
          <template #content>
            <div>
              Power provides an overview of the electrical parameters that
              determine how the LED strip is driven and protected. This section
              shows the measured input voltages, the calculated or limited
              current, the number of pixels, and the resulting maximum safe
              brightness. The controller uses these values together to estimate
              power consumption and ensure the LED strip operates within the
              limits of the connected power supplies. Adjusting any parameter in
              this section updates the others automatically, allowing the system
              to maintain safe and reliable operation while optimizing
              brightness. All new settings are written to the controller's
              retentive memory when the user clicks the Apply button. This
              action also saves all system parameters, including color
              selections, the power-on scenario, and other configuration
              options.
            </div>
          </template>
          <div class="label" style="width: 100%">
            <i class="bx bx-info-circle" />
            <input type="text" class="textlabel" value="Power" readonly />
          </div>
        </Popper>
        <!-- PI1 VOLTAGE begin -->
        <!-- Help -->
        <Popper
          class="labelStyle"
          style="position: relative; width: 70%"
          arrow
          offsetDistance="0"
          placement="bottom"
        >
          <template #content>
            <div>
              PI1 is the first power input for the LED controller. It shows the
              measured voltage supplied to the controller in volts (V). This
              voltage reading reflects the actual load conditions on the power
              line and is used by the controller to monitor power stability and
              ensure safe operation of the LED strip.
            </div>
          </template>
          <div class="label" style="width: 100%">
            <i class="bx bx-info-circle" />
            <input type="text" class="textlabel" value="PI1 Voltage" readonly />
          </div>
        </Popper>
        <!-- Field -->
        <div class="inputStyle" style="width: 30%">
          <input
            type="text"
            class="input"
            style="width: 100%; color: grey"
            :value="getPower1Voltage"
            readonly
          />
        </div>
        <!-- PI1 VOLTAGE End -->
        <!-- PI2 VOLTAGE 2 Begin -->
        <!-- Help -->
        <Popper
          class="labelStyle"
          style="position: relative; width: 70%"
          arrow
          offsetDistance="0"
          placement="bottom"
        >
          <template #content>
            <div>
              PI2 is the second power input for the LED controller. It shows the
              measured voltage supplied to the controller in volts (V). This
              voltage reading reflects the actual load conditions on the power
              line and is used by the controller to monitor power stability and
              ensure safe operation of the LED strip.
            </div>
          </template>
          <div class="label" style="width: 100%">
            <i class="bx bx-info-circle" />
            <input type="text" class="textlabel" value="PI2 Voltage" readonly />
          </div>
        </Popper>
        <!-- Field -->
        <div class="inputStyle" style="width: 30%">
          <input
            type="text"
            class="input"
            style="width: 100%; color: grey"
            :value="getPower2Voltage"
            readonly
          />
        </div>
        <!--PI2 VOLTAGE 2 End -->
        <!-- PROTECTION Begin -->
        <!-- Help -->
        <Popper
          class="labelStyle"
          style="position: relative; width: 70%"
          arrow
          offsetDistance="0"
          placement="bottom"
        >
          <template #content>
            <div>
              Protection indicates whether the controller has disabled the power
              outputs to prevent damage from excessive current or a voltage
              drop. OK means the system is operating normally. OVERLOAD means
              protection was triggered and the power outputs were temporarily
              shut down. To reset the protection, activate any scenario that
              turns the light on. If the issue persists, check the power supply
              and LED strip for faults or excessive load conditions that may be
              causing the protection to trigger.
            </div>
          </template>
          <div class="label" style="width: 100%">
            <i class="bx bx-info-circle" />
            <input type="text" class="textlabel" value="Protection" readonly />
          </div>
        </Popper>
        <!-- Field -->
        <div class="inputStyle" style="width: 30%">
          <input
            type="text"
            class="input"
            style="width: 100%"
            :style="{ color: getProtectionColor }"
            :value="getProtectionStatus"
            readonly
          />
        </div>
        <!-- PROTECTION End -->
        <!-- PIXELS Begin -->
        <!-- Help -->
        <Popper
          class="labelStyle"
          style="position: relative; width: 70%"
          arrow
          offsetDistance="0"
          placement="bottom"
        >
          <template #content>
            <div>
              Pixels is the number of addressable LEDs on the strip. Each pixel
              is one controllable unit with its own brightness and color value.
              A pixel may contain 3 or 4 internal LEDs, but it always behaves as
              a single addressable element. The controller uses the pixel count
              to estimate power consumption and to calculate safe brightness
              limits for the connected power supply. This value is taken
              directly from the layout.
            </div>
          </template>
          <div class="label" style="width: 100%">
            <i class="bx bx-info-circle" />
            <input type="text" class="textlabel" value="Pixels" readonly />
          </div>
        </Popper>
        <!-- Field -->
        <div class="inputStyle" style="width: 30%">
          <input
            type="text"
            class="input"
            style="width: 100%; color: grey"
            :value="getPixels"
            readonly
          />
        </div>
        <!-- PIXELS End -->
        <!-- LEDs Begin -->
        <!-- Help -->
        <Popper
          class="labelStyle"
          style="position: relative; width: 70%"
          arrow
          offsetDistance="0"
          placement="bottom"
        >
          <template #content>
            <div>
              LEDs is the number of individual light-emitting diodes contained
              in each pixel of the LED strip. Most RGB strips use 3 LEDs per
              pixel (GRB), while RGBW strips use 4 LEDs per pixel (GRBW). This
              value is used to calculate the total number of LEDs in the system
              and to estimate power consumption more accurately. The controller
              uses this information to determine how much current the strip can
              draw at different brightness levels and to apply safe brightness
              limits based on the available power.
            </div>
          </template>
          <div class="label" style="width: 100%">
            <i class="bx bx-info-circle" />
            <input type="text" class="textlabel" value="LEDs" readonly />
          </div>
        </Popper>
        <!-- Field -->
        <div class="inputStyle" style="width: 30%">
          <input
            type="text"
            class="input"
            style="width: 100%; color: grey"
            :value="getLeds"
            readonly
          />
        </div>
        <!-- LEDs End -->
        <!-- eLEDs Begin -->
        <!-- Help -->
        <Popper
          class="labelStyle"
          style="position: relative; width: 70%"
          arrow
          offsetDistance="0"
          placement="bottom"
        >
          <template #content>
            <div>
              eLEDs is the number of individual LEDs powered by an external
              power supply through voltage injection. These LEDs do not draw
              current from the controller's power inputs (PI1 and PI2) and are
              therefore excluded from the controller's current and
              brightness-limit calculations. The eLEDs value helps the system
              distinguish between LEDs that load the controller's power supply
              and those powered externally, ensuring accurate power estimation
              and safe brightness control.
            </div>
          </template>
          <div class="label" style="width: 100%">
            <i class="bx bx-info-circle" />
            <input type="text" class="textlabel" value="eLEDs" readonly />
          </div>
        </Popper>
        <!-- Field -->
        <div class="inputStyle" style="width: 30%">
          <input
            type="text"
            class="input"
            style="width: 100%; color: grey"
            :value="geteLeds"
            readonly
          />
        </div>
        <!-- eLEDs End -->
        <!-- iLEDs begin -->
        <!-- Help -->
        <Popper
          class="labelStyle"
          style="position: relative; width: 70%"
          arrow
          offsetDistance="0"
          placement="bottom"
        >
          <template #content>
            <div>
              iLEDs is the number of individual LEDs powered directly by the
              controller's power inputs (PI1 and PI2). Only these LEDs draw
              current from the controller's power supply and are used in all
              current and brightness calculations. LEDs powered through external
              injection are not included here. The controller uses the iLEDs
              value to estimate power consumption accurately and to apply safe
              brightness limits based on the available power from PI1 and PI2.
            </div>
          </template>
          <div class="label" style="width: 100%">
            <i class="bx bx-info-circle" />
            <input type="text" class="textlabel" value="iLEDs" readonly />
          </div>
        </Popper>
        <!-- Field -->
        <div class="inputStyle" style="width: 30%">
          <input
            type="number"
            min="0"
            class="input"
            style="width: 100%"
            :style="{ color: iLedsColor }"
            :value="getiLeds"
            @input="iLedsChange"
          />
        </div>
        <!-- iLEDs End -->
        <!-- CURRENT begin -->
        <!-- Help -->
        <Popper
          class="labelStyle"
          style="position: relative; width: 70%"
          arrow
          offsetDistance="0"
          placement="bottom"
        >
          <template #content>
            <div>
              Current shows the electrical current in amperes (A) calculated
              from the selected brightness and the number of Pixels. When the
              user enters a current limit, the controller automatically adjusts
              the maximum allowed brightness so the strip does not exceed that
              current. This protects the power supply from overload and ensures
              safe operation of the LED strip. During calibration, the
              controller determines the maximum allowed brightness and updates
              both Current and Brightness automatically to keep the system
              within the safe limits of the connected power supply.
            </div>
          </template>
          <div class="label" style="width: 100%">
            <i class="bx bx-info-circle" />
            <input type="text" class="textlabel" value="Current" readonly />
          </div>
        </Popper>
        <!-- Field -->
        <div class="inputStyle" style="width: 30%">
          <input
            type="number"
            min="1"
            :max="getMaxCurrent"
            class="input"
            style="width: 100%"
            :style="{ color: currentColor }"
            :value="getCurrent"
            @input="currentChange"
          />
        </div>
        <!-- CURRENT End -->
        <!-- BRIGHTNESS begin -->
        <!-- Help -->
        <Popper
          class="labelStyle"
          style="position: relative; width: 70%"
          arrow
          offsetDistance="0"
          placement="bottom"
        >
          <template #content>
            <div>
              Brightness sets the global light output level for the LED strip,
              expressed as a percentage. Higher brightness increases power
              consumption, while lower brightness reduces it. The controller
              automatically calculates the maximum safe brightness based on the
              current limit specified in the Current field and the number of
              Pixels, ensuring the strip does not exceed safe current levels.
              This limit updates immediately whenever the Current value changes.
              When Brightness is adjusted, the Current field updates to reflect
              the resulting power consumption based on the selected brightness
              and pixel count. During calibration, the controller determines the
              maximum allowed brightness and updates both Current and Brightness
              automatically to keep the system within the safe limits of the
              connected power supply.
            </div>
          </template>
          <div class="label" style="width: 100%">
            <i class="bx bx-info-circle" />
            <input type="text" class="textlabel" value="Brightness" readonly />
          </div>
        </Popper>
        <!-- Field -->
        <div class="inputStyle" style="width: 30%">
          <input
            type="number"
            min="1"
            :max="getMaxCalBrightness"
            class="input"
            style="width: 100%"
            :style="{ color: brightnessColor }"
            :value="getCalBrightness"
            @input="brightnessChange"
          />
        </div>
        <!-- BRIGHTNESS End -->
        <button
          id="button"
          class="button"
          v-if="apply"
          @:click="handleApplyClick"
        >
          Apply
        </button>
      </div>
    </div>
    <div v-else :style="powerStyle">
      <div class="labelStyle" style="width: 100%">
        <div class="conwaiter">Disabled</div>
      </div>
    </div>
  </div>
  <div v-else :style="powerStyle">
    <div class="labelStyle" style="width: 100%">
      <div class="conwaiter">Waiting for MQTT connection...</div>
    </div>
  </div>
</template>
<script>
import AppConstants from "@/AppConstants.js";
import { mapGetters, mapMutations } from "vuex";
import Popper from "vue3-popper";
const MENU_WIDTH = AppConstants.MENU_WIDTH;
let size = AppConstants.get2Size();
export default {
  data() {
    return {
      powerStyle: {
        position: "absolute",
        background: "black",
        border: "1px solid black",
        left: MENU_WIDTH + "px",
        top: "0vh",
        width: size.width + "vw",
        height: "100vh",
        color: "white",
      },
      apply: false,
      brightnessApply: false,
      currentApply: false,
      iLedsApply: true,
      timerColor: "lightgrey",
      currentColor: "lightgrey",
      brightnessColor: "lightgrey",
      iLedsColor: "lightgrey",
    };
  },
  components: {
    Popper,
  },
  computed: {
    ...mapGetters({
      mqttInstance: "mqttStore/getMqttInstance",
      getMqttIsInstanceConnected: "mqttStore/getMqttIsInstanceConnected",
      getMqttUserTopic: "mqttStore/getMqttUserTopic",
      getTimer: "lidarStore/getTimer",
      getPower1Voltage: "powerStore/getPower1Voltage",
      getPower2Voltage: "powerStore/getPower2Voltage",
      getPowerRefVoltage: "powerStore/getPowerRefVoltage",
      getPowerState: "powerStore/getPowerState",
      getCalBrightness: "powerStore/getCalBrightness",
      getMaxCalBrightness: "powerStore/getMaxCalBrightness",
      getPixels: "powerStore/getPixels",
      getLeds: "powerStore/getLeds",
      getiLeds: "powerStore/getiLeds",
      geteLeds: "powerStore/geteLeds",
      getCurrent: "powerStore/getCurrent",
      getMaxCurrent: "powerStore/getMaxCurrent",
      getProtectionStatus: "powerStore/getProtectionStatus",
      getProtectionColor: "powerStore/getProtectionColor",
      getMode: "configStore/getMode",
    }),
  },
  methods: {
    ...mapMutations({
      setTimer: "lidarStore/setTimer",
      setCurrent: "powerStore/setCurrent",
      setCalBrightness: "powerStore/setCalBrightness",
      setMaxCalBrightness: "powerStore/setMaxCalBrightness",
      setiLeds: "powerStore/setiLeds",
      seteLeds: "powerStore/seteLeds",
    }),
    checkParameters: function () {
      if (this.currentApply && this.brightnessApply && this.iLedsApply) {
        this.apply = true;
      } else {
        this.apply = false;
      }
    },
    handleApplyClick: function () {
      /*
      let current = this.getCurrent;
      let ledCurrent = this.getiLeds * 0.02;
      let brightness = Math.round((100 * current) / ledCurrent);
      this.calculateCurrent(brightness);
      */
      /* -- */
      let message;
      message = "{";
      message += "'name':'power'";
      message += ",'ileds':" + this.getiLeds;
      message += ",'current':" + this.getCurrent;
      message += ",'brightness':" + this.getCalBrightness;
      message += "}";
      this.mqttInstance.publish(
        this.getMqttUserTopic + "/update",
        message,
        0,
        {},
        () => {}
      );
      /* -- */
      this.apply = false;
    },
    calculateCurrent: function (brightness) {
      if (brightness > this.getMaxCalBrightness) {
        brightness = this.getMaxCalBrightness;
      }
      let ledCurrent = this.getiLeds * 0.02;
      let current = Math.round((ledCurrent * brightness) / 100);
      if (this.getMaxCurrent < current) {
        current = this.getMaxCurrent;
      }
      /* -- */
      this.currentColor = "lightgrey";
      this.currentApply = true;
      this.setCurrent(current);
      /* -- */
      this.brightnessColor = "lightgrey";
      this.brightnessApply = true;
      this.setCalBrightness(brightness);
      /* -- */
      this.checkParameters();
      return current;
    },
    currentChange: function (event) {
      let brightness;
      let current;
      let isUpdateCurrent = true;
      if (!isNaN(event.target.value)) {
        current = event.target.value;
      } else {
        current = 0;
      }
      if (current <= 0 || current > this.getMaxCurrent) {
        brightness = 0;
        current = 0;
        this.currentColor = "red";
        this.brightnessColor = "red";
        this.currentApply = false;
        this.brightnessApply = false;
        this.setCalBrightness(brightness);
        this.setCurrent(current);
      } else {
        /* Calculate brightness based on current */
        let ledCurrent = this.getiLeds * 0.02;
        brightness = Math.round((100 * current) / ledCurrent);
        if (brightness > this.getMaxCalBrightness) {
          brightness = this.getMaxCalBrightness;
          isUpdateCurrent = false;
        }
        if (brightness > 100) {
          brightness = 100;
          isUpdateCurrent = false;
        }
        /* -- */
        this.brightnessColor = "lightgrey";
        this.brightnessApply = true;
        this.setCalBrightness(brightness);
        /* Re-calculate current based on brightness */
        if (isUpdateCurrent) {
          this.calculateCurrent(brightness);
        }
      }
      this.checkParameters();
    },
    brightnessChange: function (event) {
      let brightness;
      let current;
      if (!isNaN(event.target.value)) {
        brightness = event.target.value;
      } else {
        brightness = 0;
      }
      if (brightness <= 0 || brightness > this.getMaxCalBrightness) {
        brightness = 0;
        current = 0;
        this.currentColor = "red";
        this.brightnessColor = "red";
        this.currentApply = false;
        this.brightnessApply = false;
        this.setCalBrightness(brightness);
        this.setCurrent(current);
      } else {
        /* Calculate current based on brightness */
        this.calculateCurrent(brightness);
      }
      this.checkParameters();
    },
    iLedsChange: function (event) {
      let iLeds = this.getiLeds;
      let eLeds = this.geteLeds;
      if (!isNaN(event.target.value)) {
        iLeds = event.target.value;
      } else {
        this.iLedsColor = "red";
        this.iLedsApply = false;
        this.checkParameters();
        return;
      }
      if (iLeds > this.getLeds) {
        this.setiLeds(iLeds);
        this.iLedsColor = "red";
        this.iLedsApply = false;
        this.checkParameters();
        return;
      }
      /* Calculate eLeds */
      eLeds = this.getLeds - iLeds;
      this.iLedsApply = true;
      this.iLedsColor = "lightgrey";
      this.seteLeds(eLeds);
      this.setiLeds(iLeds);
      /* Recalculate max brightness based on new iLeds */
      let ledCurrent = this.getiLeds * 0.02;
      let maxCalBrightness = Math.round(
        (100 * this.getMaxCurrent) / ledCurrent
      );
      if (maxCalBrightness > this.getMaxCalBrightness) {
        maxCalBrightness = 100;
      }
      this.setMaxCalBrightness(maxCalBrightness);
      /* Recalculate current based on new iLeds */
      this.calculateCurrent(maxCalBrightness);
      this.checkParameters();
    },
    updatePowerInfo: function () {
      if (this.getMqttIsInstanceConnected) {
        let message = "{";
        message += "'name':'powerinfo'";
        message += "}";
        this.mqttInstance.publish(
          this.getMqttUserTopic + "/update",
          message,
          0,
          {},
          () => {}
        );
      }
    },
  },
  created() {
    this.updatePowerInfo();
  },
  mounted() {},
  watch: {},
};
</script>
<style scoped>
.input {
  color: white;
  background-color: black;
  font-size: large;
  margin: 1vh 0vw 1vh 0vw;
  border: 3px solid #303030;
  padding: 1px;
  border-radius: 25px;
  text-align: center;
}
.input:hover {
  border: 3px solid white;
}
.input:focus {
  border: 3px solid white;
  outline: none;
}
.label {
  position: relative;
  color: gray;
  background-color: black;
  caret-color: transparent;
  font-size: large;
  margin: 1vh 0vw 1vh 0vw;
  border: 3px solid #303030;
  padding: 1px;
  border-radius: 25px;
  text-align: center;
}
.label:hover {
  border: 3px solid white;
}
.label:focus {
  border: 3px solid white;
  outline: none;
}
.label1 {
  font-size: normal;
  color: lightgrey;
}
.button {
  color: white;
  border: 3px solid #303030;
  background-color: black;
  text-align: center;
  text-decoration: none;
  font-size: large;
  transition-duration: 0.4s;
  cursor: pointer;
  position: center;
  width: 100%;
  margin: 1vh 0vw 1vh 0vw;
  padding: 10px;
  border-radius: 25px;
}
.button:hover {
  border: 3px solid white;
}
.selector {
  color: white;
  border: 3px solid #303030;
  background-color: black;
  text-align: center;
  font-size: large;
  transition-duration: 0.4s;
  cursor: pointer;
  position: center;
  margin: 1vh 0vw 1vh 0vw;
  padding: 1px;
  border-radius: 25px;
  width: 100%;
}
.selector:focus {
  border: 3px solid #303030;
  outline: none;
}
.selector:hover {
  border: 3px solid white;
}
.labelStyle {
  position: relative;
  text-align: left;
  float: left;
  --popper-theme-background-color: white;
  --popper-theme-background-color-hover: white;
  --popper-theme-text-color: black;
  --popper-theme-border-width: 0px;
  --popper-theme-border-style: solid;
  --popper-theme-border-radius: 25px;
  --popper-theme-padding: 10px;
}
.inputStyle {
  position: relative;
  background: black;
  border: 1px solid black;
  color: white;
  text-align: left;
  float: left;
}
.conwaiter {
  color: white;
  font-size: x-large;
  margin: auto;
  width: 100%;
  border: 3px solid gray;
  padding: 10px;
  text-align: center;
}
.textlabel {
  color: gray;
  background-color: black;
  font-size: large;
  margin: 0vh 1vw 0vh 1vw;
  border: none;
  outline: none;
  text-align: center;
}
</style>
